import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Globe, Plus, RefreshCw, Trash2, CheckCircle, Clock, 
  AlertTriangle, Copy, ExternalLink, Shield, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending_verification: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
  verified: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: CheckCircle },
  active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle },
  failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle }
};

const SSL_STATUS_COLORS = {
  pending: { bg: 'bg-slate-500/20', text: 'text-slate-400' },
  provisioning: { bg: 'bg-amber-500/20', text: 'text-amber-400' },
  active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  failed: { bg: 'bg-red-500/20', text: 'text-red-400' }
};

export default function SuperAdminDomains() {
  const [domains, setDomains] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [dnsDialogOpen, setDnsDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [verifying, setVerifying] = useState(null);
  
  const [formData, setFormData] = useState({
    tenant_id: '',
    custom_domain: '',
    ssl_enabled: true,
    redirect_www: true
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDomains();
    fetchTenants();
  }, []);

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/branding/admin/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data);
    } catch (error) {
      toast.error('Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/platform/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.post(
        `${API}/branding/admin/tenants/${formData.tenant_id}/domain`,
        {
          custom_domain: formData.custom_domain,
          ssl_enabled: formData.ssl_enabled,
          redirect_www: formData.redirect_www
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Custom domain added');
      setAddDialogOpen(false);
      setFormData({ tenant_id: '', custom_domain: '', ssl_enabled: true, redirect_www: true });
      fetchDomains();
      
      // Show DNS records
      setSelectedDomain(response.data);
      setDnsDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleVerify = async (tenantId) => {
    setVerifying(tenantId);
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.post(
        `${API}/branding/admin/tenants/${tenantId}/domain/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.verified) {
        toast.success('Domain verified and SSL provisioned!');
      } else {
        toast.warning(response.data.message);
      }
      fetchDomains();
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('Are you sure you want to remove this custom domain?')) return;
    
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.delete(`${API}/branding/admin/tenants/${tenantId}/domain`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Domain removed');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const showDnsRecords = (domain) => {
    setSelectedDomain(domain);
    setDnsDialogOpen(true);
  };

  // Filter tenants that don't have a domain yet
  const availableTenants = tenants.filter(
    t => !domains.some(d => d.tenant_id === t.id)
  );

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Custom Domains
          </h1>
          <p className="text-slate-400 mt-1">Manage white-label domains for your tenants</p>
        </div>
        <Button 
          className="bg-violet-600 hover:bg-violet-700 text-white"
          onClick={() => setAddDialogOpen(true)}
          disabled={availableTenants.length === 0}
          data-testid="add-domain-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Domain
        </Button>
      </div>

      {/* Info Card */}
      <Alert className="bg-slate-800/50 border-violet-500/50">
        <Globe className="h-4 w-4 text-violet-400" />
        <AlertDescription className="text-slate-300">
          Custom domains allow tenants to use their own domain (e.g., hr.acme.com) instead of the default Recruitizr URL.
          DNS records must be configured by the domain owner.
        </AlertDescription>
      </Alert>

      {/* Domains Table */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          ) : domains.length === 0 ? (
            <div className="text-center py-16">
              <Globe className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No custom domains configured</p>
              <Button 
                className="mt-4 bg-violet-600 hover:bg-violet-700"
                onClick={() => setAddDialogOpen(true)}
              >
                Add First Domain
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Domain</TableHead>
                  <TableHead className="text-slate-400">Tenant</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">SSL</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((domain) => {
                  const StatusIcon = STATUS_COLORS[domain.status]?.icon || Clock;
                  return (
                    <TableRow key={domain.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-violet-600/20 rounded">
                            <Globe className="h-4 w-4 text-violet-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{domain.custom_domain}</p>
                            <button 
                              onClick={() => showDnsRecords(domain)}
                              className="text-xs text-violet-400 hover:underline"
                            >
                              View DNS Records
                            </button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-300 text-sm">{domain.tenant_name}</p>
                        <p className="text-xs text-slate-500">{domain.tenant_slug}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[domain.status]?.bg} ${STATUS_COLORS[domain.status]?.text}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {domain.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${SSL_STATUS_COLORS[domain.ssl_status]?.bg} ${SSL_STATUS_COLORS[domain.ssl_status]?.text}`}>
                          <Lock className="h-3 w-3 mr-1" />
                          {domain.ssl_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {new Date(domain.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {domain.status === 'pending_verification' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                              onClick={() => handleVerify(domain.tenant_id)}
                              disabled={verifying === domain.tenant_id}
                            >
                              <RefreshCw className={`h-4 w-4 mr-1 ${verifying === domain.tenant_id ? 'animate-spin' : ''}`} />
                              Verify
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => handleDelete(domain.tenant_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Domain Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add Custom Domain</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure a custom domain for white-label access
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDomain} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 text-xs font-bold uppercase">Select Tenant</Label>
              <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Choose a tenant" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                  {availableTenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs font-bold uppercase">Custom Domain</Label>
              <Input
                value={formData.custom_domain}
                onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase() })}
                className="mt-2 bg-slate-800 border-slate-600 text-white"
                placeholder="hr.acme.com"
                required
              />
              <p className="text-xs text-slate-500 mt-1">Enter the full domain without http://</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="border-slate-600 text-slate-300" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700 text-white">
                Add Domain
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DNS Records Dialog */}
      <Dialog open={dnsDialogOpen} onOpenChange={setDnsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-400" />
              DNS Configuration Required
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Add the following DNS records to your domain provider
            </DialogDescription>
          </DialogHeader>
          {selectedDomain && (
            <div className="space-y-4 mt-4">
              <Alert className="bg-amber-500/10 border-amber-500/50">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-amber-300">
                  DNS changes may take up to 48 hours to propagate. Click "Verify" once records are added.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-3">
                {selectedDomain.dns_records?.map((record, idx) => (
                  <div key={`supe-${idx}`} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-violet-500/20 text-violet-400">{record.type}</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-400 hover:text-white"
                        onClick={() => copyToClipboard(record.value)}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Name/Host:</span>
                        <p className="text-white font-mono mt-1">{record.name}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Value/Points to:</span>
                        <p className="text-white font-mono mt-1 break-all">{record.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" className="border-slate-600" onClick={() => setDnsDialogOpen(false)}>
                  Close
                </Button>
                {selectedDomain.status === 'pending_verification' && (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      handleVerify(selectedDomain.tenant_id);
                      setDnsDialogOpen(false);
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Verify Now
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
